/* ALPINULIN · Relaunch 2026 · Produktseite: Galerie, Kaufart Einmalig/Abo, Mengenkarten,
   Mengenfeld mit Tipp, „Passt gut dazu“, Sticky-Leiste, Warenkorb.
   Warenkorb über /cart/add.js mit mehreren Zeilen (Horizon-Standard kann nur eine Zeile mit einem Plan). */
import { CartLinesUpdateEvent } from '@shopify/events';

const FORMAT_FALLBACK = '{{amount_with_comma_separator}} €';

export function formatMoney(cents, format) {
  const fmt = format || FORMAT_FALLBACK;
  const n = Math.round(Number(cents) || 0) / 100;
  const de = (value, decimals) =>
    value.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals_with_comma_separator':
        return de(n, 0);
      case 'amount_with_comma_separator':
        return de(n, 2);
      case 'amount_no_decimals':
        return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
      default:
        return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  });
}

/** Legt Zeilen in den Warenkorb und aktualisiert Zähler und Drawer (gleicher Weg wie die Angebots-Leiter). */
export async function addLines(host, items) {
  const deferred = CartLinesUpdateEvent.createPromise?.();
  if (deferred) {
    host.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
  }
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
      }),
    });
    const result = await response.json();
    if (result.status) {
      deferred?.reject?.(new Error(result.message || 'cart add failed'));
      throw new Error(result.description || result.message || 'cart add failed');
    }
    const cart = await (await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })).json();
    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: result.sections, source: 'alpl-pdp', itemCount: items.length, didError: false },
      });
    }
    document.dispatchEvent(new CustomEvent('alpl:cart-updated', { detail: { cart } }));
    return cart;
  } catch (error) {
    deferred?.reject?.(error);
    throw error;
  }
}

class AlplPdp extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.format = this.dataset.moneyFormat || FORMAT_FALLBACK;
    this.price = Number(this.dataset.price);
    this.tiers = this.dataset.tiers ? JSON.parse(this.dataset.tiers) : [];
    this.aboLines = this.dataset.aboLines ? JSON.parse(this.dataset.aboLines) : null;
    this.aboTotal = Number(this.dataset.aboTotal || 0);
    this.mode = 'once';
    // qty = Anzahl Flaschen insgesamt; die Mengenkarten setzen sie nur (5 Flaschen → qty 5)
    this.qty = 1;
    try {
      this.sticky = JSON.parse(this.querySelector('[data-sticky-data]')?.textContent || '{}');
    } catch (e) {
      this.sticky = {};
    }

    this.addEventListener('click', (event) => this.#onClick(event));
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-addon]')) this.#render();
    });
    this.#initSticky();
    this.#initSwipe();
    this.#render();
  }

  /** Galerie: auf dem großen Bild nach links/rechts wischen (Handy) */
  #initSwipe() {
    const stage = this.querySelector('.alpl-pdp__stage');
    const count = this.querySelectorAll('[data-slide]').length;
    if (!stage || count < 2) return;
    let x0 = null;
    let y0 = 0;
    stage.addEventListener('touchstart', (e) => {
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
    }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      const dy = e.changedTouches[0].clientY - y0;
      x0 = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      const cur = Number(this.querySelector('[data-slide].is-active')?.dataset.slide || 0);
      this.#showSlide((cur + (dx < 0 ? 1 : -1) + count) % count);
    }, { passive: true });
  }

  #onClick(event) {
    const t = event.target.closest('button');
    if (!t || !this.contains(t)) return;
    if (t.dataset.thumb !== undefined) return this.#showSlide(Number(t.dataset.thumb));
    if (t.dataset.mode || t.dataset.modeLink) {
      this.mode = t.dataset.mode || t.dataset.modeLink;
      return this.#render();
    }
    if (t.dataset.pack) {
      this.qty = Number(t.dataset.pack);
      return this.#render();
    }
    if (t.dataset.qty) {
      this.qty = Math.min(20, Math.max(1, this.qty + Number(t.dataset.qty)));
      return this.#render();
    }
    if (t.hasAttribute('data-tip-take')) {
      if (this.tipTarget) {
        this.qty = this.tipTarget;
      }
      return this.#render();
    }
    if (t.hasAttribute('data-atc')) return this.#add(t);
  }

  #showSlide(i) {
    this.querySelectorAll('[data-slide]').forEach((slide) => {
      const on = Number(slide.dataset.slide) === i;
      slide.hidden = !on;
      slide.classList.toggle('is-active', on);
      const video = slide.querySelector('video');
      if (video) {
        if (on) {
          video.muted = true;
          video.play?.().catch(() => {});
        } else {
          video.pause?.();
        }
      }
    });
    this.querySelectorAll('[data-thumb]').forEach((b) => {
      const on = Number(b.dataset.thumb) === i;
      b.setAttribute('aria-selected', String(on));
      if (on) b.parentElement.scrollTo({ left: b.offsetLeft - b.parentElement.offsetLeft - 10, behavior: 'smooth' });
    });
  }

  /** Warenkorbpreis bei Menge n: Shopify nimmt je Zeile den besten EINEN Mengenrabatt (Prozentrabatt je Stück abgerundet, wie Shopify). */
  #total(n) {
    const full = this.price * n;
    let best = 0;
    for (const t of this.tiers) {
      if (n < t.min) continue;
      let off = t.pct ? Math.floor((this.price * t.pct) / 100) * n : t.each ? t.each * n : t.amount || 0;
      off = Math.min(off, full);
      if (off > best) best = off;
    }
    return full - best;
  }

  #onceTotal() {
    const n = this.qty;
    let total = this.#total(n);
    this.querySelectorAll('[data-addon]:checked').forEach((box) => (total += Number(box.dataset.addonPrice)));
    return total;
  }

  #render() {
    const abo = this.mode === 'abo' && this.aboLines;
    this.querySelectorAll('[data-mode]').forEach((b) => {
      const on = b.dataset.mode === (abo ? 'abo' : 'once');
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', String(on));
    });
    const once = this.querySelector('[data-panel="once"]');
    const aboPanel = this.querySelector('[data-panel="abo"]');
    if (once) once.hidden = !!abo;
    if (aboPanel) aboPanel.hidden = !abo;

    this.querySelectorAll('[data-pack]').forEach((b) => {
      const on = Number(b.dataset.pack) === this.qty;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    });
    const qv = this.querySelector('[data-qty-value]');
    if (qv) qv.textContent = String(this.qty);

    const onceTotal = this.#onceTotal();
    const label = abo
      ? `${this.dataset.labelAbo} · ${formatMoney(this.aboTotal, this.format)}`
      : `${this.dataset.labelCart} · ${formatMoney(onceTotal, this.format)}`;
    if (this.dataset.available !== 'false') {
      this.querySelectorAll('.alpl-pdp__panel [data-atc]').forEach((b) => {
        if (!b.classList.contains('is-added')) b.textContent = label;
      });
    }

    // Tipp bei 2 Einzelstücken: nur zeigen, wenn die nächste Kartenstufe je Flasche günstiger ist
    const tipBox = this.querySelector('[data-tip-box]');
    if (tipBox) {
      const cards = [...this.querySelectorAll('[data-pack]')].map((b) => Number(b.dataset.pack)).filter((x) => x > 2);
      const next = cards[0];
      let show = false;
      if (!abo && next && this.qty === 2) {
        const packPrice = this.#total(next);
        const more = packPrice - this.#total(2);
        show = more < this.price * (next - 2);
        if (show) {
          tipBox.querySelector('[data-tip-text]').textContent = (this.dataset.tip || '')
            .replace('{n}', `${next} ${this.dataset.unitPlural || ''}`.trim())
            .replace('{preis}', formatMoney(packPrice, this.format))
            .replace('{mehr}', formatMoney(more, this.format));
          this.tipTarget = next;
        }
      }
      tipBox.hidden = !show;
    }

    // Sticky
    const line = this.querySelector('[data-sticky-line]');
    if (line) {
      const n = this.qty;
      line.textContent = abo
        ? `${this.sticky.rating || ''}${this.sticky.abo || ''}${formatMoney(this.aboTotal, this.format)} ${this.sticky.perDelivery || ''}`
        : `${this.sticky.rating || ''}${n} ${n === 1 ? this.sticky.unit || '' : this.dataset.unitPlural || ''} · ${formatMoney(this.#total(n), this.format)}`;
    }
    const cta = this.querySelector('[data-sticky-cta]');
    if (cta && this.dataset.available !== 'false' && !cta.classList.contains('is-added')) {
      cta.textContent = abo ? this.dataset.labelAbo : this.dataset.labelCart;
    }
  }

  #items() {
    if (this.mode === 'abo' && this.aboLines) {
      const offer = this.dataset.offer;
      return this.aboLines.map((line) => ({ ...line, properties: { ...(line.properties || {}), _alp_offer: offer } }));
    }
    const items = [{ id: Number(this.dataset.variant), quantity: this.qty }];
    this.querySelectorAll('[data-addon]:checked').forEach((box) => items.push({ id: Number(box.dataset.addon), quantity: 1 }));
    return items;
  }

  async #add(button) {
    const error = this.querySelector('[data-error]');
    if (error) error.hidden = true;
    const buttons = this.querySelectorAll('[data-atc]');
    buttons.forEach((b) => {
      b.disabled = true;
      b.classList.add('is-loading');
    });
    try {
      await addLines(this, this.#items());
      buttons.forEach((b) => {
        b.classList.add('is-added');
        b.textContent = this.dataset.labelAdded;
      });
      setTimeout(() => {
        buttons.forEach((b) => b.classList.remove('is-added'));
        this.#render();
      }, 2500);
    } catch (e) {
      if (error) {
        error.textContent = e?.message && e.message !== 'cart add failed' ? e.message : this.dataset.labelError;
        error.hidden = false;
      }
    } finally {
      buttons.forEach((b) => {
        b.disabled = false;
        b.classList.remove('is-loading');
      });
    }
  }

  #initSticky() {
    const bar = this.querySelector('[data-sticky]');
    const target = this.querySelector('.alpl-pdp__info');
    if (!bar || !target || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelectorAll('button').forEach((b) => (b.tabIndex = show ? 0 : -1));
    });
    io.observe(target);
  }
}

if (!customElements.get('alpl-pdp')) customElements.define('alpl-pdp', AlplPdp);

/* Karten-Buttons „Hinzufügen“ (Ritual, Abo-Seite): data-alpl-add="<variant-id>" [data-alpl-plan="<plan-id>"] */
if (!window.__alplAddBound) {
  window.__alplAddBound = true;
  document.addEventListener('click', async (event) => {
    const b = event.target.closest('[data-alpl-add], [data-alpl-lines]');
    if (!b) return;
    event.preventDefault();
    let items;
    if (b.dataset.alplLines) {
      items = JSON.parse(b.dataset.alplLines).map((line) =>
        b.dataset.alplOffer ? { ...line, properties: { ...(line.properties || {}), _alp_offer: b.dataset.alplOffer } } : line
      );
    } else {
      const item = { id: Number(b.dataset.alplAdd), quantity: Number(b.dataset.alplQty || 1) };
      if (b.dataset.alplPlan) item.selling_plan = Number(b.dataset.alplPlan);
      items = [item];
    }
    const original = b.dataset.label || b.textContent;
    b.dataset.label = original;
    b.disabled = true;
    try {
      await addLines(b, items);
      b.textContent = b.dataset.added || '✓';
    } catch (e) {
      b.textContent = b.dataset.error || '!';
    } finally {
      b.disabled = false;
      setTimeout(() => (b.textContent = original), 2500);
    }
  });
}
